import { useEffect, useRef, useState } from "react";
import authTenantApis from "src/api/authTenant";
import { IDENTIFIER_MIN_LENGTH, IDENTIFIER_MAX_LENGTH } from "src/utils/authFormValidation";

export type IdentifierAvailabilityStatus =
  | "idle"
  | "invalid"
  | "checking"
  | "available"
  | "taken";

const DEBOUNCE_MS = 500;

// Debounced "is this organization identifier free?" check, shared by the
// self-serve sign-up wizard and the admin tenant-creation wizard — both
// hit the same public /app/auth/check-identifier endpoint. Doesn't call
// the API until the value clears the min-length bar.
const useIdentifierAvailability = (rawValue: string) => {
  const [status, setStatus] = useState<IdentifierAvailabilityStatus>("idle");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    const value = rawValue.trim().toLowerCase().replace(/\s+/g, "-");

    if (!value) {
      setStatus("idle");
      setSuggestions([]);
      setError(null);
      return;
    }
    if (value.length < IDENTIFIER_MIN_LENGTH) {
      setStatus("invalid");
      setSuggestions([]);
      setError(`Must be at least ${IDENTIFIER_MIN_LENGTH} characters`);
      return;
    }
    if (value.length > IDENTIFIER_MAX_LENGTH) {
      setStatus("invalid");
      setSuggestions([]);
      setError(`Must be under ${IDENTIFIER_MAX_LENGTH} characters`);
      return;
    }

    setStatus("checking");
    setError(null);
    const mySeq = ++requestSeq.current;

    const timer = setTimeout(async () => {
      try {
        const res: any = await authTenantApis.checkIdentifier(value);
        if (mySeq !== requestSeq.current) return; // a newer keystroke superseded this
        if (res?.available) {
          setStatus("available");
          setSuggestions([]);
        } else {
          setStatus("taken");
          setSuggestions(res?.suggestions || []);
        }
      } catch {
        if (mySeq !== requestSeq.current) return;
        setStatus("idle");
        setSuggestions([]);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [rawValue]);

  return { status, suggestions, error };
};

export default useIdentifierAvailability;
